import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import PageHeading from '~/components/PageHeading';
import {
    setSpecificReportQuery,
    useFormsQuery,
    useSpecificReportQuery,
} from '~/lib/Queries';
import { ReportType, type Report } from '../ReportsIndexPage/Report.types';
import TStrings from './ReportsEdit.i18n';
import { GridExposedMethods } from '~/components/Grid';
import useBusinessPutApi from '~/lib/useBusinessPutApi';
import useBusinessNavigate from '~/lib/useBusinessNavigate';
import Loading from '~/components/Loading';
import TemplateEditor from '~/pages/business/ReportsIndexPage/ReportTemplateModal/TemplateEditor';
import Modal from '~/components/Modal';
import useBusinessDeleteApi from '~/lib/useBusinessDeleteApi';
import ReportGridFactory from './ReportGrid/ReportGridFactory';
import FormsSelect from './FormsSelect';
import { Form } from '~/lib/Api.types';
import {
    gridFiltersToSelectedTextArray,
    selectedFormNamesToForms,
    selectedTextArrayToGridFilter,
} from './ReportGrid/Grid.utils';
import { FORM_NAME_COLUMN_KEY } from './ReportGrid/FormsGrid';
import * as Toast from '~/lib/Toast';

const ReportEditPage = () => {
    const { reportId } = useParams();
    const { state } = useLocation();
    const {
        data: reportRemote,
        refetch,
        isLoading,
    } = useSpecificReportQuery(reportId);
    const report: Report = reportRemote ?? state?.report;
    const { update, isUpdating } = useBusinessPutApi(`/reports`);
    const navigate = useBusinessNavigate();
    const grid = useRef<GridExposedMethods>(null);
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editNameModalVisible, setEditNameModalVisible] = useState(false);
    const { deleteRequest, isDeleting } = useBusinessDeleteApi(`/reports`);
    const { data: formList } = useFormsQuery();

    const saveReportCallback = useCallback(async () => {
        const exportedFilterSettings = grid.current?.exportSettings();
        if (report?.id && exportedFilterSettings) {
            const updatedData = {
                settings: {
                    ...report?.settings,
                    ...exportedFilterSettings,
                },
            };

            setIsSaving(true);
            setSpecificReportQuery(report.id, {
                ...report,
                ...updatedData,
            });
            try {
                const updatedReport = await update(report.id, updatedData);
                if (updatedReport) {
                    console.log('Updated report = ', updatedReport);
                    setSpecificReportQuery(report.id, updatedReport);
                    Toast.success('Successfully saved view');
                    //navigate('/reports');
                }
                setIsSaving(false);
            } catch (e) {
                setIsSaving(false);
                throw e;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [report?.id]);

    const exportCsvCallback = useCallback(() => {
        if (grid.current) {
            grid.current?.downloadCsvData();
        }
    }, []);

    const editNameCallback = useCallback(() => {
        setEditNameModalVisible(true);
    }, []);

    const onGridViewChanged = useCallback(() => {
        setIsDirty(true);
    }, []);

    const onReportNameEdited = useCallback(
        async (newReport: Report) => {
            const reportId = newReport?.id || report?.id;
            if (reportId) {
                const updatedReport = await update(reportId, {
                    name: newReport.name,
                });
                if (updatedReport) {
                    setEditNameModalVisible(false);
                    refetch();
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [report?.id],
    );

    const onReportDeleteClick = useCallback(
        async (newReport: Report) => {
            const reportId = newReport?.id || report?.id;
            if (reportId) {
                await deleteRequest(reportId);
                refetch();
                navigate('/reports');
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [report?.id],
    );

    const onEditNameCancel = useCallback(() => {
        setEditNameModalVisible(false);
    }, []);

    const [selectedForms, setSelectedForms] = useState<Array<Form>>(() =>
        selectedFormNamesToForms(
            gridFiltersToSelectedTextArray(
                FORM_NAME_COLUMN_KEY,
                report?.settings,
            ),
            formList,
        ),
    );

    useEffect(() => {
        // every time report settings change
        const selectedFormsCalculatedFromSettings = selectedFormNamesToForms(
            gridFiltersToSelectedTextArray(
                FORM_NAME_COLUMN_KEY,
                report?.settings,
            ),
            formList,
        );
        setSelectedForms(selectedFormsCalculatedFromSettings);
    }, [report?.settings, formList]);

    const onFormSelected = useCallback(
        (newSelectedForms: Array<Form>) => {
            setSelectedForms(newSelectedForms);

            // create grid filters based on form name selection
            const newFilters = selectedTextArrayToGridFilter(
                FORM_NAME_COLUMN_KEY,
                newSelectedForms.map(v => v.name),
                report?.settings?.filterModel,
            );

            if (newFilters) {
                // if new filters were created
                grid.current?.importSettings({
                    filterModel: newFilters, // only apply the new filters
                }); // apply them
            }
        },
        [report?.settings],
    );

    const isFormsReport = report?.type === ReportType.Forms;

    const formTemplate =
        report?.settings?.templateSelected ??
        state?.templateSelected ??
        state?.report?.settings?.templateSelected;
    return (
        <div>
            <PageHeading
                heading={report?.name}
                crumbs={[
                    {
                        name: 'Reporting',
                        href: '/reports',
                    },
                ]}
                className={`mb-${isFormsReport ? 6 : 4}`}
                headerClassName="!mt-0 !mb-0"
                limitWidth={false}
                headerSibling={
                    <div>
                        <button
                            disabled={isLoading || isUpdating}
                            type="button"
                            onClick={editNameCallback}
                            className="btn-light-pill-sm uppercase"
                        >
                            {TStrings.EDIT_TEMPLATE_BUTTON}
                        </button>
                    </div>

                }
                // leftContent={
                //     isFormsReport ? (
                //         <FormsSelect
                //             id="formsSelectComponent"
                //             selectedValues={selectedForms}
                //             onChange={onFormSelected}
                //         />
                //     ) : null
                // }
                rightContent={
                    <div className="flex flex-row grow items-center justify-between">
                        <div className="flex flex-row grow items-center justify-end">
                            <button
                                disabled={isLoading || isUpdating || !isDirty}
                                type="button"
                                onClick={saveReportCallback}
                                className={`${!isDirty ? 'btn-gray-pill' : 'btn-pill'}`}
                            >
                                {isSaving
                                    ? TStrings.SAVING_TEMPLATE_BUTTON
                                    : TStrings.SAVE_TEMPLATE_BUTTON}
                            </button>
                            <button
                                disabled={isLoading || isUpdating}
                                type="button"
                                onClick={exportCsvCallback}
                                className="btn-light-pill mx-2"
                            >
                                {TStrings.EXPORT_CSV_TEMPLATE_BUTTON}
                            </button>
                        </div>
                    </div>
                }
            />
            {isLoading || isDeleting || formTemplate == null ? (
                <Loading />
            ) : (
                <ReportGridFactory
                    type={report?.type}
                    ref={grid}
                    initialSettings={report?.settings}
                    onViewChanged={onGridViewChanged}
                    templateSelected={formTemplate}
                />
            )}
            <Modal
                isOpen={editNameModalVisible}
                onClose={() => {
                    setEditNameModalVisible(false);
                }}
            >
                <TemplateEditor
                    reportToEdit={report}
                    onCancel={onEditNameCancel}
                    isLoading={isUpdating || isDeleting}
                    onDelete={onReportDeleteClick}
                    onEdit={onReportNameEdited}
                />
            </Modal>
        </div>
    );
};

export default ReportEditPage;
